import {
  KeyboardEvent,
  PointerEvent,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export type OverlaySide = "left" | "right";

/**
 * Which bottom corner the clinician keeps the overlay in. A per-browser
 * preference, not part of the appointment, so it lives in localStorage apart
 * from the sessionStorage-backed appointment state (appointmentOverlayStorage).
 */
export const OVERLAY_SIDE_STORAGE_KEY = "spire360.appointmentOverlay.side";

/** A release faster than this (px/ms) throws the card in that direction. */
const FLICK_VELOCITY = 0.5;

const loadSide = (): OverlaySide => {
  try {
    return window.localStorage.getItem(OVERLAY_SIDE_STORAGE_KEY) === "left"
      ? "left"
      : "right";
  } catch {
    return "right";
  }
};

const saveSide = (side: OverlaySide) => {
  try {
    window.localStorage.setItem(OVERLAY_SIDE_STORAGE_KEY, side);
  } catch {
    // Storage unavailable -- the choice still holds for this page view.
  }
};

type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  lastX: number;
  lastT: number;
  velocityX: number;
};

/**
 * Drag-to-dock for the appointment overlay: the card follows the pointer from
 * its grip, and on release snaps to the half of the screen it's over -- or,
 * if flicked, the direction it was thrown. Arrow keys on the grip do the same.
 * The move between corners animates from where the card was released (FLIP).
 */
export function useOverlayDock() {
  const [side, setSideState] = useState<OverlaySide>(loadSide);
  const [dragging, setDragging] = useState(false);
  const cardRef = useRef<HTMLElement | null>(null);
  const drag = useRef<DragState | null>(null);
  const flipFrom = useRef<DOMRect | null>(null);

  const moveTo = useCallback((next: OverlaySide) => {
    const card = cardRef.current;
    if (card) flipFrom.current = card.getBoundingClientRect();
    setSideState(next);
    saveSide(next);
  }, []);

  // After the corner changes, start the card where it was and let the CSS
  // transition carry it into its new corner.
  useLayoutEffect(() => {
    const card = cardRef.current;
    const from = flipFrom.current;
    flipFrom.current = null;
    if (!card || !from) return;

    card.style.transition = "none";
    card.style.transform = "";
    const to = card.getBoundingClientRect();
    card.style.transform = `translate(${from.left - to.left}px, ${
      from.top - to.top
    }px)`;
    // Force the start position to be applied before re-enabling the transition.
    void card.offsetWidth;
    card.style.transition = "";
    card.style.transform = "";
  }, [side]);

  const onPointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
    if (event.button !== 0 || !cardRef.current) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drag.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastT: event.timeStamp,
      velocityX: 0,
    };
    cardRef.current.style.transition = "none";
    setDragging(true);
  }, []);

  const onPointerMove = useCallback((event: PointerEvent<HTMLElement>) => {
    const state = drag.current;
    const card = cardRef.current;
    if (!state || !card || event.pointerId !== state.pointerId) return;

    card.style.transform = `translate(${event.clientX - state.startX}px, ${
      event.clientY - state.startY
    }px)`;

    const dt = event.timeStamp - state.lastT;
    if (dt > 0) {
      const instant = (event.clientX - state.lastX) / dt;
      state.velocityX = state.velocityX * 0.3 + instant * 0.7;
      state.lastX = event.clientX;
      state.lastT = event.timeStamp;
    }
  }, []);

  const endDrag = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const state = drag.current;
      const card = cardRef.current;
      if (!state || !card || event.pointerId !== state.pointerId) return;
      drag.current = null;
      setDragging(false);

      const rect = card.getBoundingClientRect();
      const centre = rect.left + rect.width / 2;
      const target: OverlaySide =
        Math.abs(state.velocityX) > FLICK_VELOCITY
          ? state.velocityX < 0
            ? "left"
            : "right"
          : centre < window.innerWidth / 2
          ? "left"
          : "right";

      if (target !== side) {
        moveTo(target);
      } else {
        // Not moving corners: glide back.
        card.style.transition = "";
        card.style.transform = "";
      }
    },
    [moveTo, side]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === "ArrowLeft" && side !== "left") {
        event.preventDefault();
        moveTo("left");
      } else if (event.key === "ArrowRight" && side !== "right") {
        event.preventDefault();
        moveTo("right");
      }
    },
    [moveTo, side]
  );

  return {
    side,
    dragging,
    cardRef,
    gripProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown,
    },
  };
}
